import type { ReactNode, FC } from "react";
import { Provider as JotaiProvider } from "jotai";
import { ConfettiProvider } from "@/providers/confettiProvider";
import { ProProvider } from "@/providers/proProvider";
import { MobileProvider } from "@/providers/mobileProvider";
import { MobileNavbarProvider } from "@/providers/mobileNavbarProvider";
import { DesktopNavbarProvider } from "@/providers/desktopNavbarProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/providers/queryProvider";
import { IntlProvider } from "@/providers/intlProvider";
import { SentryProvider } from "@/providers/sentryProvider";
import { IAPProvider } from "@/providers/IAPProvider";
import { InstantProvider } from "@/providers/InstantProvider";
import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack";
import { PushNotificationProvider } from "@/hooks/notifications/usePushNotification";
import { PreferencesProvider } from "@/providers/preferencesProvider";
import { PlayBillingProvider } from "@/providers/PlayBillingProvider";

// Group providers by their purpose for better organization
const AppStateProviders: FC<{ children: ReactNode }> = ({ children }) => (
  <JotaiProvider>
    <InstantProvider>
      <PreferencesProvider>
        <PushNotificationProvider>{children}</PushNotificationProvider>
      </PreferencesProvider>
    </InstantProvider>
  </JotaiProvider>
);

const UIProviders: FC<{ children: ReactNode }> = ({ children }) => (
  <ConfettiProvider>
    <MobileNavbarProvider>
      <DesktopNavbarProvider>{children}</DesktopNavbarProvider>
    </MobileNavbarProvider>
  </ConfettiProvider>
);

const FeatureProviders: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ProProvider>
      <IAPProvider>
        <PlayBillingProvider>
          <IntlProvider>{children}</IntlProvider>
        </PlayBillingProvider>
      </IAPProvider>
    </ProProvider>
  );
};

export const Providers: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <SentryProvider>
      <QueryClientProvider client={queryClient}>
        <AuthQueryProvider>
          <MobileProvider>
            <AppStateProviders>
              <UIProviders>
                <FeatureProviders>{children}</FeatureProviders>
              </UIProviders>
            </AppStateProviders>
          </MobileProvider>
        </AuthQueryProvider>
      </QueryClientProvider>
    </SentryProvider>
  );
};
